import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {PlaceOrderRequestBody} from '@app/model/order/dcp-place-order-body.model';
import {
  ArrayService,
  ConfigService,
  ConfiguratorUriParameterService,
  DcpApiService,
  DcpGlobalMessageService,
  DcpPriceData,
  HttpRequestOptions,
  JsonService,
  LocalStorageService,
  MomentService,
  SessionStorageService,
  TransactionType,
} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Store} from '@ngrx/store';
import {CartActions} from '@spartacus/cart/base/core';
import {Cart, CartAddEntrySuccessEvent, OrderEntry} from '@spartacus/cart/base/root';

import {BaseSiteService, EventService, Product, RoutingService, User, UserIdService} from '@spartacus/core';
import {Order, OrderFacade, OrderPlacedEvent} from '@spartacus/order/root';

import {UserAccountFacade} from '@spartacus/user/account/root';
import moment, {Moment} from 'moment';
import {BehaviorSubject, combineLatest, Observable, of, ReplaySubject, throwError} from 'rxjs';
import {catchError, concatMap, first, map, mergeMap, switchMap, tap} from 'rxjs/operators';
import {DcpCartConfigurationForm, DcpCartDecoratorModel, KeyValuePair} from '../cart/dcp-cart-configuration.model';
import {OrderHistoryProcessingErrorService} from '../checkout/error-handling/order-history-processing-error/order-history-processing-error.service';
import {CartDecoratorErrorHandlingService} from '../checkout/services/cart-decorator-error-handling.service';
import {CardPaymentSessionStorageService} from '../payment/services/card-payment-session-storage.service';
import {DcpCcPspService} from '../payment/services/dcp-cc-psp.service';
import {PaymentError} from '../payment/services/error-handling/payment-error.enum';
import {PaymentState} from '../payment/services/store/payment-state';
import {PlaceOrderErrorEnum} from '../place-order/error-handling/place-order-error.enum';
import {ConfiguratorProductService} from '../product/configurator/services/configurator-product.service';
import {PlansAndPricingDataService} from '../product/details/plans-and-pricing/plans-and-pricing-data.service';
import {DcpProductUtilitiesService} from '../product/services/dcp-product-utilities.service';
import {DcpProductService} from '../product/services/dcp-product.service';
import {EntryGroupService} from '../product/services/entry-group.service';
import {EddlOrderCompleteEvent} from './analytics/EDDL/events/eddl-order-complete-event';
import {CampaignTrackingService} from './campaign-tracking.service';
import {DcpActiveCartService} from './dcp-active-cart.service';
import {DcpKymaService} from './dcp-kyma.service';
import {OrderConfirmationPageService} from './order/order-confirmation-page.service';
import {DcpCurrencyService} from './pricing/dcp-currency.service';
import {TransactionTypeService} from './transactionTypes/transaction-type.service';

export const CONFIGURATION_KEY: string = 'configuration';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpCartDecoratorService {
  public priceDataSubject: ReplaySubject<DcpPriceData> = new ReplaySubject<DcpPriceData>(1);
  /**
   * The currency should not be sent in the first /configure call.
   * The 'configure' endpoint will figure out the currency of the cart and return it in its response.
   */
  public currency: string;
  public providedByOrderPlaced = false;
  public selectedCurrency: string;
  /**
   * Variable to store value if the place order request is currently pending
   *
   * @private
   */
  private isPlaceOrderPendingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(PLATFORM_ID) public platformId: object,
    private http: HttpClient,
    private uriParamService: ConfiguratorUriParameterService,
    private activeCartService: DcpActiveCartService,
    private entryGroupService: EntryGroupService,
    private jsonService: JsonService,
    private baseSiteService: BaseSiteService,
    private kymaService: DcpKymaService,
    private momentService: MomentService,
    private campaignTrackingService: CampaignTrackingService,
    private orderHistoryProcessingErrorService: OrderHistoryProcessingErrorService,
    private store: Store<{}>,
    private cartDecoratorErrorHandlingService: CartDecoratorErrorHandlingService,
    private userIdService: UserIdService,
    private configService: ConfigService,
    private configuratorProductService: ConfiguratorProductService,
    private dcpCcPspService: DcpCcPspService,
    private userFacade: UserAccountFacade,
    private currencyService: DcpCurrencyService,
    private transactionTypeService: TransactionTypeService,
    private dcpProductService: DcpProductService,
    private dcpGlobalMessageService: DcpGlobalMessageService,
    private cardPaymentHelperService: CardPaymentSessionStorageService,
    private orderConfirmationService: OrderConfirmationPageService,
    private plansAndPricingDataService: PlansAndPricingDataService,
    private dcpProductUtilitiesService: DcpProductUtilitiesService,
    private eventService: EventService,
    private orderFacade: OrderFacade,
    private routingService: RoutingService,
    private dcpApiService: DcpApiService,
    private localStorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService
  ) {
    //set initial value of ReplaySubject
    this.priceDataSubject.next(null);
  }

  get isPlaceOrderPending$(): Observable<boolean> {
    return this.isPlaceOrderPendingSubject
      .asObservable()
      .pipe(
        tap((isPlaceOrderPending) =>
          this.dcpGlobalMessageService.toggleMessageGroupVisibility('dateAutoCorrection', isPlaceOrderPending)
        )
      );
  }

  get addToCartUrl(): string {
    return this.getUrl('addToCartV2');
  }

  get configureUrl(): string {
    return this.getUrl('configureV2');
  }

  getUrl(key: string): string {
    return this.kymaService.getUrl('cartDecorator') + this.configService.getString('kyma.cartDecorator.' + key);
  }

  setIsPlaceOrderPending(isPending: boolean): void {
    this.isPlaceOrderPendingSubject.next(isPending);
  }

  /**
   * @param product the product
   * @param cartConfigurationForm the data used in the cart decorator
   */
  public addToCart(
    product: Product,
    cartConfigurationForm: DcpCartConfigurationForm
  ): Observable<CartAddEntrySuccessEvent> {
    return this.priceDataSubject.pipe(
      first(),
      concatMap((configuratorModel) => {
        this.setEndDate(configuratorModel?.configuration?.contract?.endDate, cartConfigurationForm);
        return combineLatest([
          this.kymaService.prepareAuthConfig(),
          this.userIdService.getUserId(),
          this.activeCartService.requireLoadedCart().pipe(switchMap(() => this.activeCartService.getActiveCart())),
          this.baseSiteService.getActive(),
          this.enhanceCartConfigurationModel(product, cartConfigurationForm),
        ]).pipe(
          first(),
          concatMap(([config, userId, cartState, baseSiteId, cartDecoratorModel]) => {
            if (this.campaignTrackingService.getCampaignData()) {
              this.extractCampaignAttributes(cartDecoratorModel);
            }
            const cartId: string = this.activeCartService.getCartIdByUserId(cartState, userId);
            config.params = {cartId, userId, baseSiteId};
            config.body = cartDecoratorModel;
            let requestType = 'POST';
            if (cartDecoratorModel?.entryNumber !== undefined) {
              requestType = 'PUT';
            }
            this.addTechnicalNameByProduct(product, cartDecoratorModel);
            return this.http.request<CartAddEntrySuccessEvent>(requestType, this.addToCartUrl, config);
          })
        );
      })
    );
  }

  public extractCampaignAttributes(cartDecoratorModel: DcpCartDecoratorModel) {
    if (!cartDecoratorModel) {
      return;
    }
    cartDecoratorModel.data.push({
      type: 'marketingAttributes',
      value: JSON.stringify(
        this.campaignTrackingService.toCodeValuePair(this.campaignTrackingService.getCampaignData())
      ),
    });
  }

  public startPaymentFlow(purchaseOrderNumber: string): Observable<string | Order> {
    return this.activeCartService.getActiveCart().pipe(
      first(),
      untilDestroyed(this),
      switchMap((cart) => {
        this.isPlaceOrderPendingSubject.next(true);
        if (this.activeCartService.isCardPayment(cart)) {
          this.dcpCcPspService.startCcFlow();
          return this.placeCreditCardOrder(purchaseOrderNumber);
        } else {
          return this.placeInvoiceOrder(purchaseOrderNumber);
        }
      })
    );
  }

  public placeCreditCardOrder(purchaseOrderNumber: string): Observable<string | Order> {
    return combineLatest([
      this.userIdService.getUserId(),
      this.activeCartService.getActiveCart(),
      this.dcpCcPspService.convertStateToPlaceOrderPayload().pipe(
        catchError((state) => {
          this.dcpGlobalMessageService.addMessage(state, this?.constructor?.name, 'errorLogging');
          return this.handlePaymentProviderErrors(state);
        })
      ),
    ]).pipe(
      first(),
      concatMap(([userId, cart, body]) => {
        const cartId = this.activeCartService.getCartIdByUserId(cart, userId);
        return this.placeOrderCall(cartId, userId, purchaseOrderNumber, body);
      }),
      untilDestroyed(this)
    );
  }

  public placeInvoiceOrder(purchaseOrderNumber: string): Observable<string | Order> {
    return combineLatest([this.userIdService.getUserId(), this.activeCartService.getActiveCart()]).pipe(
      first(),
      concatMap(([userId, cart]) => {
        const cartId = this.activeCartService.getCartIdByUserId(cart, userId);
        return this.placeOrderCall(cartId, userId, purchaseOrderNumber);
      }),
      untilDestroyed(this)
    );
  }

  placeOrderCall(
    cartId: string,
    userId: string,
    purchaseOrderNumber: string,
    body?: PlaceOrderRequestBody
  ): Observable<Order | string> {
    return this.baseSiteService.getActive().pipe(
      first(),
      switchMap((baseSiteId: string) =>
        this.dcpApiService
          .callKyma<Order>(
            'cartDecorator',
            'placeOrderV2',
            {},
            {
              body,
              params: {
                purchaseOrderNumber,
                cartId,
                userId,
                baseSiteId,
              },
            },
            'POST'
          )
          .pipe(
            tap((order) => this.afterPlaceOrder(order, cartId, userId)),
            catchError((error: HttpErrorResponse) => {
              this.dcpGlobalMessageService.addMessage(error, this?.constructor?.name, 'errorLogging');
              if (error.status) {
                if (this.orderHistoryProcessingErrorService.shouldHandleError(error)) {
                  this.orderHistoryProcessingErrorService.showError();
                  this.isPlaceOrderPendingSubject.next(false);
                  throw PlaceOrderErrorEnum;
                }
                this.cartDecoratorErrorHandlingService.handle(error, this).then(() => {
                  // error handling finished
                  this.isPlaceOrderPendingSubject.next(false);
                });
              } else {
                this.isPlaceOrderPendingSubject.next(false);
              }
              return of('Exception already handled');
            })
          )
      )
    );
  }

  handlePaymentProviderErrors(state: PaymentState): Observable<never> {
    this.dcpCcPspService.displayPaymentError(state);
    if (!this.isPriceCalculationError(state)) {
      this.dcpCcPspService.saveAuthorizationErrors(state);
    }
    this.isPlaceOrderPendingSubject.next(false);
    throw PaymentError;
  }

  isPriceCalculationError(state: PaymentState): boolean {
    return state.error === PaymentError.NON_ZERO_GROSS_VALUE_NO_CALCULATION_BREAKDOWN;
  }

  /**
   * @param product the product code
   * @param cartConfigurationForm the data used in the cart decorator
   */
  public cartConfigure(product: Product, cartConfigurationForm: DcpCartConfigurationForm): Observable<DcpPriceData> {
    return this.kymaService.prepareAuthConfig().pipe(
      mergeMap(
        (config: HttpRequestOptions): Observable<DcpPriceData> =>
          this.enhanceCartConfigurationModel(product, cartConfigurationForm).pipe(
            first(),
            mergeMap((cartDecoratorModel) => {
              this.addTechnicalNameByProduct(product, cartDecoratorModel);
              return this.http.post<DcpPriceData>(this.configureUrl, cartDecoratorModel, config);
            }),
            untilDestroyed(this)
          )
      ),
      tap((pd) => {
        this.emitPriceData(pd);
        this.currency = pd.configuration?.contract?.currency;
      }),
      catchError((e) => {
        this.dcpGlobalMessageService.addMessage(e, this?.constructor?.name, 'errorLogging');
        this.cartDecoratorErrorHandlingService.handle(e, this);
        return throwError(e);
      })
    );
  }

  /**
   * @param product the product
   * @param cartConfig the data used in the cart decorator
   */
  enhanceCartConfigurationModel(
    product: Product,
    cartConfig: DcpCartConfigurationForm
  ): Observable<DcpCartDecoratorModel> {
    const cartConfigurationModel: DcpCartDecoratorModel = {};
    cartConfigurationModel.transactionType = this.uriParamService.getAction() || cartConfig.transactionType;
    return combineLatest([
      this.transactionTypeService.getTransactionTypeConfiguration(cartConfigurationModel.transactionType),
      this.currencyService.getActive(),
      this.isProductCpea(product),
    ]).pipe(
      untilDestroyed(this),
      first(),
      switchMap(([transactionType, activeCurrency, isProductCpea]) => {
        this.selectedCurrency = this.selectCurrency(activeCurrency, transactionType);
        return this.collectCartConfigModelConfig(
          product,
          cartConfig?.data,
          this.selectedCurrency,
          cartConfig['eu-access']
        ).pipe(
          switchMap((configurationValue) => {
            cartConfigurationModel.product = {
              code: product.supplierAlternativeAID,
            };
            cartConfigurationModel.bundleTemplateCode = product.supplierAlternativeAID;
            cartConfigurationModel.quantity = +cartConfig.quantity;
            if (cartConfig?.entryNumber !== undefined) {
              cartConfigurationModel.entryNumber = +cartConfig?.entryNumber;
            }

            cartConfigurationModel.data = [];
            if (isProductCpea) {
              return this.configureSpendAmount(cartConfig, cartConfigurationModel, product, configurationValue);
            } else {
              cartConfigurationModel.data.push({
                type: CONFIGURATION_KEY,
                value: JSON.stringify(configurationValue),
              });
              return this.addDerivativeSupplierAlternativeIds(product.supplierAlternativeAID, cartConfigurationModel);
            }
          })
        );
      })
    );
  }

  public isProductCpea(product?: Product): Observable<boolean> {
    if (product) {
      return of(this.checkProductDataForCpeaCommercialModel(product));
    }
    return this.configuratorProductService
      .getVariant$()
      .pipe(map((product) => this.checkProductDataForCpeaCommercialModel(product)));
  }

  /**
   * cartConfigurationModel is enhanced by key value attributes.
   *
   * @param product the product for the form
   * @param cartConfigurationFormData data from the form
   * @param currency current currency
   * @param euAccess eu-access coming from form
   */
  collectCartConfigModelConfig(
    product: Product,
    cartConfigurationFormData: KeyValuePair<any>,
    currency: string,
    euAccess?: boolean
  ): Observable<KeyValuePair<string>> {
    return combineLatest([
      this.configuratorProductService.isDisplayProductLoaded$(),
      this.dcpProductService.getProductCodesBySupplierAlternativeAid(product.supplierAlternativeAID),
    ]).pipe(
      first(),
      mergeMap(([isDisplayProductLoaded, productCodes]) => {
        if (!isDisplayProductLoaded) {
          this.configuratorProductService.setDisplayProduct(productCodes.displayProductCode);
        }

        return combineLatest([this.configuratorProductService.getDisplay$(), this.userFacade.get()]).pipe(
          map(([product, user]) =>
            this.parseConfigModelStartDate(
              this.assignConfigValues(cartConfigurationFormData, user, product, currency, euAccess)
            )
          )
        );
      })
    );
  }

  assignConfigValues(
    cartConfigurationFormData: KeyValuePair<string>,
    user: User,
    product: Product,
    currency: string,
    euAccess: boolean
  ): KeyValuePair<string> {
    const configuration: KeyValuePair<string> = Object.assign({}, cartConfigurationFormData);
    configuration.product = this.uriParamService.getProduct() || configuration.product;
    configuration.productName = this.uriParamService.getProductName() || configuration.productName;
    configuration.sProduct = this.uriParamService.getSourceProduct() || configuration.sProduct;
    configuration.sProductName = this.uriParamService.getSourceProductName() || configuration.sProductName;
    // @ts-ignore
    configuration.account = this.uriParamService.getAccount() || user?.orgUnit?.sapAccountID || configuration.account;
    configuration.contact = this.uriParamService.getContact() || configuration.contact;
    configuration.itemId = this.uriParamService.getItemId() || configuration.itemId;
    configuration.contract = this.uriParamService.getContract() || configuration.contract;
    configuration.itemNumber = this.uriParamService.getContractItemNumber() || configuration.itemNumber;
    configuration.transactionType = this.uriParamService.getAction() || configuration.transactionType;
    configuration.duration = this.jsonService.parseSafely(configuration.duration) || configuration.duration;
    configuration.vendorId = product?.vendor?.code;

    if (currency) {
      configuration.currency = currency;
    }
    if (euAccess !== undefined) {
      configuration['EU-Access'] = euAccess.toString();
    }

    return configuration;
  }

  /**
   * Checks if a cart has an entry with productCode
   * and returns a DcpCartConfigurationForm filled with the cart data if entry was found.
   * If the product is not in the cart empty DcpCartConfigurationForm object will be returned
   * {entryNumber: undefined, quantity: undefined}.
   *
   * @param productCode to search for in the cart
   */
  getCartConfigurationFormForProduct(productCode: string): Observable<DcpCartConfigurationForm> {
    this.activeCartService.getActive(); //sustains authorized cart api calls
    return this.activeCartService.takeActive().pipe(
      map((cart) => this.getCartEntries(cart)),
      map((entries) => this.getEntryByCode(entries, productCode)),
      map((entry) => this.createCartConfigurationForm(entry))
    );
  }

  emitPriceData(pd: DcpPriceData) {
    this.priceDataSubject.next(this.jsonService.parseSafely(JSON.stringify(pd)));
  }

  /**
   * Creates a CartConfigurationForm and fills it with the relevant data from entry
   *
   * @param entry object ot retrieve the data from
   */
  createCartConfigurationForm(entry: OrderEntry): DcpCartConfigurationForm {
    const cartConfigurationForm: DcpCartConfigurationForm = {
      entryNumber: entry?.entryNumber,
      quantity: entry?.quantity,
      data: this.extractConfigurationJson(entry),
    };

    if (cartConfigurationForm.data.startDate) {
      const startDate: Moment = moment(cartConfigurationForm.data.startDate);

      cartConfigurationForm.data.startDate = this.momentService.momentToNgbDateStruct(startDate);
    }

    if (cartConfigurationForm.data.duration) {
      cartConfigurationForm.data.duration = JSON.stringify(cartConfigurationForm.data.duration);
    }

    cartConfigurationForm.transactionType = cartConfigurationForm?.data?.transactionType;

    return cartConfigurationForm;
  }

  extractConfigurationJson(entry: {data?: KeyValuePair<any>}): KeyValuePair<any> {
    if (entry?.data) {
      const value: string = entry?.data?.find((x) => x.type === CONFIGURATION_KEY)?.value;

      if (value) {
        return this.jsonService.parseSafely(value);
      }
    }
    return {};
  }

  private afterPlaceOrder(order: Order, cartId: string, userId: string): void {
    this.orderConfirmationService.clear();
    this.cardPaymentHelperService.clear();
    this.eventService.dispatch(new EddlOrderCompleteEvent());
    this.store.dispatch(new CartActions.RemoveCart({cartId}));
    this.orderFacade.setPlacedOrder(order);
    this.eventService.dispatch(
      {
        userId,
        cartId,
        /**
         * As we know the cart is not anonymous (precondition checked),
         * we can safely use the cartId, which is actually the cart.code.
         */
        cartCode: cartId,
        order,
      },
      OrderPlacedEvent
    );
    this.isPlaceOrderPendingSubject.next(false);

    this.sessionStorageService.removeItem('backUrl');
    const successUrl = this.localStorageService.getItem('successUrl');
    if (successUrl) {
      this.localStorageService.removeItem('successUrl');
      window.location.href = successUrl;
      return;
    }

    this.routingService.go({cxRoute: 'orderConfirmation'});
  }

  private getCartEntries(cart: Cart): OrderEntry[] {
    if (!cart?.entries) {
      return [];
    }
    return cart.entries;
  }

  private getEntryByCode(entries: OrderEntry[], code: String): OrderEntry {
    const entry = entries.find((entry) => entry?.product?.code === code);
    return entry || {};
  }

  private addTechnicalNameByProduct(product: Product, cartDecoratorModel: DcpCartDecoratorModel) {
    cartDecoratorModel.product.technicalName = this.dcpProductUtilitiesService.getProductTechnicalName(
      product,
      cartDecoratorModel.bundleTemplateCode
    );
  }

  private configureSpendAmount(
    cartConfig: DcpCartConfigurationForm,
    cartConfigurationModel: DcpCartDecoratorModel,
    product: Product,
    configurationValue: KeyValuePair<string>
  ): Observable<DcpCartDecoratorModel> {
    configurationValue.spendAmount = cartConfig.spendAmount.toString();
    cartConfigurationModel.quantity = 1;
    cartConfigurationModel.data.push({
      type: CONFIGURATION_KEY,
      value: JSON.stringify(configurationValue),
    });
    return this.addDerivativeSupplierAlternativeIds(product.supplierAlternativeAID, cartConfigurationModel);
  }

  private checkProductDataForCpeaCommercialModel(product: Product): boolean {
    const commercialModel = this.plansAndPricingDataService.getCommercialModel(product.data);
    return commercialModel?.name === 'CPEA';
  }

  /**
   * Gets the derivative Material Ids and adds them to the cartConfigurationModel.
   *
   * @param supplierAlternativeAID code of the current product
   * @param cartConfigurationModel DcpCartDecoratorModel
   */
  private addDerivativeSupplierAlternativeIds(
    supplierAlternativeAID: string,
    cartConfigurationModel: DcpCartDecoratorModel
  ): Observable<DcpCartDecoratorModel> {
    return this.entryGroupService.getDerivativeSupplierAlternativeIds(supplierAlternativeAID).pipe(
      map((mIds) => {
        if (mIds.length) {
          cartConfigurationModel.componentProducts = ArrayService.removeDuplicates(mIds);
        }
        return cartConfigurationModel;
      })
    );
  }

  /**
   * parses and sets the start date if its valid
   *
   * @param data containing start date as NgbDateStruct
   */
  private parseConfigModelStartDate(data: KeyValuePair<any>): KeyValuePair<any> {
    if (data.startDate) {
      const d: moment.Moment = this.momentService.ngbDateToMoment(data.startDate);
      data.startDate = d.isValid() ? d.format(moment.HTML5_FMT.DATE) : '';
    }
    return data;
  }

  private setEndDate(endDate: string, cartConfigurationForm: DcpCartConfigurationForm): void {
    if (endDate && cartConfigurationForm) {
      cartConfigurationForm.data.endDate = endDate;
    }
  }

  private selectCurrency(activeCurrency: string, transactionType: TransactionType): string {
    if (transactionType?.shouldPrefillCartFromContract) {
      return this.currency || activeCurrency;
    } else {
      return activeCurrency;
    }
  }
}
